/**
 * Section shadowing — peer feedback and moderation Firestore schemas + paths.
 *
 * One feedback document per reviewer, per section, per week, living alongside
 * the other section-analysis leaf collections:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/shadowFeedback/{reviewerUid}`
 *
 * A sibling, server-written moderation verdict gates whether the reviewee may
 * read the feedback (mirrors `studentReflectionModeration`):
 * `.../sectionWeeks/{sectionWeek}/shadowFeedbackModeration/{reviewerUid}`
 *
 * A second sibling holds flags to course staff: a reviewer's flag about the
 * section, kept off the feedback doc so the reviewee cannot read it, and a
 * reviewee's flag about the review, which the reviewer cannot read:
 * `.../sectionWeeks/{sectionWeek}/shadowStaffFlags/{reviewerUid}` and
 * `.../sectionWeeks/{sectionWeek}/shadowStaffFlags/reviewee__{reviewerUid}`
 *
 * Feedback fields are config-driven: `content` is keyed by rubric field key and
 * validated against the rubric named by `rubricVersion` (see
 * `section/shadowRubric.ts`). The `{courseId}` path segment lets Firestore
 * rules perform course-role checks without a cross-document `get()`.
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../../types';
import {ShadowFieldValueSchema} from '../../../section/shadowRubric';
import type {SectionId, SectionWeekId} from '../../../section/sectionApi';
import type {CourseId} from '../../../types/branded';
import {
  getSectionAnalysisWeekChildCollectionPath,
  SECTION_ANALYSIS_SHADOW_FEEDBACK_MODERATION_SUBCOLLECTION,
  SECTION_ANALYSIS_SHADOW_FEEDBACK_SUBCOLLECTION,
  SECTION_ANALYSIS_SHADOW_STAFF_FLAGS_SUBCOLLECTION,
} from './sectionAnalysisFirestorePaths';

/**
 * Must match the Cloud Functions export name (Firebase callable id) that
 * returns assignment-gated shadow materials (transcript + signed video URL).
 */
export const GET_SHADOW_MATERIALS_FUNCTION_NAME = 'getshadowmaterials';

/** One transcript line surfaced to the shadow view. */
export const ShadowTranscriptLineSchema = z.object({
  /** Speaker label parsed from the transcript. */
  speaker: z.string(),
  /** Whether this speaker is the section leader being reviewed. */
  isLeader: z.boolean(),
  /** Line start timestamp (e.g. `HH:MM:SS`). */
  timestamp: z.string(),
  /** Spoken text for this line. */
  text: z.string(),
});

/** One transcript line surfaced to the shadow view. */
export type ShadowTranscriptLine = z.infer<typeof ShadowTranscriptLineSchema>;

/** One in-meeting chat message sent to everyone, from Zoom's chat file. */
export const ShadowChatLineSchema = z.object({
  /** Display name of the sender. */
  sender: z.string(),
  /**
   * Whether the sender's Zoom name matches the transcript's leader, who is
   * picked the same way as for transcript lines, not from the roster.
   */
  isLeader: z.boolean(),
  /** Offset from the segment's start (`HH:MM:SS`), like a transcript line. */
  timestamp: z.string(),
  /** Message text; a multi-line message keeps its line breaks. */
  text: z.string(),
});

/** One in-meeting chat message surfaced to the shadow view. */
export type ShadowChatLine = z.infer<typeof ShadowChatLineSchema>;

/** Callable request payload for `getshadowmaterials`. */
export const GetShadowMaterialsRequestSchema = z.object({
  /** Course the assignment belongs to. */
  courseId: z.string().min(1),
  /** The assignment authorizing access to these materials. */
  assignmentId: z.string().min(1),
});

/** Request payload for `getshadowmaterials`. */
export type GetShadowMaterialsRequest = z.infer<
  typeof GetShadowMaterialsRequestSchema
>;

/** One recording rendering (Zoom view) with a short-lived signed URL. */
export const ShadowVideoSchema = z.object({
  /** Human-readable view label (e.g. "Gallery", "Screen + speaker"). */
  label: z.string(),
  /** Short-lived signed URL to this rendering's mp4. */
  url: z.string(),
  /**
   * Zoom `recording_type` (e.g. `shared_screen`, `active_speaker`) so the player
   * can pick the screen share as the main view and overlay the speaker; null if
   * unknown.
   */
  recordingType: z.string().nullable(),
  /**
   * ISO 8601 segment start, for aligning/ordering renderings (multi-segment
   * host-rejoin recordings); null if unknown.
   */
  recordingStart: z.string().nullable(),
});

/** One recording rendering with a signed URL. */
export type ShadowVideo = z.infer<typeof ShadowVideoSchema>;

/**
 * One recording segment — a single continuous Zoom recording block. A section
 * where the host dropped and rejoined produces multiple segments, each with its
 * own screen/speaker/gallery renderings and its own transcript, whose VTT
 * timestamps are relative to that segment's start. Grouping renderings by
 * segment keeps a segment's videos aligned with each other and with its
 * transcript (so click-to-seek lands on the right frame).
 */
export const ShadowSegmentSchema = z.object({
  /** ISO 8601 start of this segment, for ordering and labeling; null if unknown. */
  recordingStart: z.string().nullable(),
  /**
   * Segment length in seconds (recordingEnd − recordingStart); null when either
   * bound is unknown.
   */
  durationSeconds: z.number().nullable(),
  /**
   * Signed URLs for this segment's mp4 renderings (screen / speaker / gallery),
   * most useful view first. Older recordings whose view type was not labeled
   * surface as generic "Recording N".
   */
  videos: z.array(ShadowVideoSchema),
  /** Parsed transcript lines for this segment; empty if it has no transcript. */
  transcript: z.array(ShadowTranscriptLineSchema),
  /**
   * Chat messages sent to everyone during this segment; empty if none were
   * saved. Absent from servers deployed before chat was surfaced, and from
   * recordings whose loader does not read chat (office hours).
   */
  chat: z.array(ShadowChatLineSchema).optional(),
});

/** One recording segment with signed videos and its transcript. */
export type ShadowSegment = z.infer<typeof ShadowSegmentSchema>;

/** Callable response for `getshadowmaterials`. */
export const GetShadowMaterialsResponseSchema = z.object({
  /**
   * Recording segments in chronological order. A host rejoin yields more than
   * one; a single-take section yields one. Empty if no recording exists yet.
   */
  segments: z.array(ShadowSegmentSchema),
});

/** Response for `getshadowmaterials`. */
export type GetShadowMaterialsResponse = z.infer<
  typeof GetShadowMaterialsResponseSchema
>;

/**
 * Must match the Cloud Functions export name (Firebase callable id) that
 * returns a section week's recording to the people who taught it (the section
 * leader, that week's cover) and to course staff, for the SL debrief.
 */
export const GET_SECTION_DEBRIEF_MATERIALS_FUNCTION_NAME =
  'getsectiondebriefmaterials';

/** Callable request payload for `getsectiondebriefmaterials`. */
export const GetSectionDebriefMaterialsRequestSchema = z.object({
  /** Course the section belongs to; must match the section document. */
  courseId: z.string().min(1),
  /** The section whose recording is wanted. */
  sectionId: z.string().min(1),
  /** The 1-based section week. */
  sectionWeek: z.number().int().min(1),
});

/** Request payload for `getsectiondebriefmaterials`. */
export type GetSectionDebriefMaterialsRequest = z.infer<
  typeof GetSectionDebriefMaterialsRequestSchema
>;

/**
 * A part of a section week whose Zoom meeting ran but whose recording has not
 * arrived yet (see `findPendingRecordingParts`).
 */
export const PendingRecordingPartSchema = z.object({
  /** ISO 8601 time the part's Zoom meeting started. */
  startedAt: z.string(),
  /** ISO 8601 time it ended; null while it is still running. */
  endedAt: z.string().nullable(),
});

/** A section week part whose recording has not arrived yet. */
export type PendingRecordingPart = z.infer<typeof PendingRecordingPartSchema>;

/**
 * Callable response for `getsectiondebriefmaterials`: the same recording
 * segments as `getshadowmaterials`, plus the parts still waiting on Zoom.
 */
export const GetSectionDebriefMaterialsResponseSchema =
  GetShadowMaterialsResponseSchema.extend({
    /**
     * Parts of the week whose recording has not arrived yet, earliest first.
     * Absent from servers deployed before it was added; read that as none.
     */
    pendingParts: z.array(PendingRecordingPartSchema).optional(),
  });

/** Response for `getsectiondebriefmaterials`. */
export type GetSectionDebriefMaterialsResponse = z.infer<
  typeof GetSectionDebriefMaterialsResponseSchema
>;

/**
 * Must match the Cloud Functions export name (Firebase callable id) that
 * returns the recording for a nominated "Teaching Star" moment, gated on the
 * caller being a section leader in the course (not a party to any assignment).
 */
export const GET_FEATURED_MOMENT_MATERIALS_FUNCTION_NAME =
  'getfeaturedmomentmaterials';

/** Callable request payload for `getfeaturedmomentmaterials`. */
export const GetFeaturedMomentMaterialsRequestSchema = z.object({
  /** Course the nomination belongs to. */
  courseId: z.string().min(1),
  /** The nomination authorizing (and locating) the recording to watch. */
  nominationId: z.string().min(1),
});

/** Request payload for `getfeaturedmomentmaterials`. */
export type GetFeaturedMomentMaterialsRequest = z.infer<
  typeof GetFeaturedMomentMaterialsRequestSchema
>;

/**
 * Callable response for `getfeaturedmomentmaterials`. Same recording segments
 * shape as `getshadowmaterials` — the caller already holds the nominated
 * moment's timestamps from the nomination doc and seeks the player to them.
 */
export const GetFeaturedMomentMaterialsResponseSchema = z.object({
  /** Recording segments in chronological order (see `getshadowmaterials`). */
  segments: z.array(ShadowSegmentSchema),
});

/** Response for `getfeaturedmomentmaterials`. */
export type GetFeaturedMomentMaterialsResponse = z.infer<
  typeof GetFeaturedMomentMaterialsResponseSchema
>;

// ============================================================================
// Feedback document
// ============================================================================

/** Lifecycle of a peer feedback document. */
export const ShadowFeedbackStatusSchema = z.enum(['draft', 'submitted']);

/** Lifecycle status of a peer feedback document. */
export type ShadowFeedbackStatus = z.infer<typeof ShadowFeedbackStatusSchema>;

/**
 * Moderation state of a submitted feedback document. Only `approved` feedback
 * is readable by the reviewee; `needs_review` routes to the TA+ queue. Defined
 * here (before the feedback doc) so the feedback doc can denormalize it.
 */
export const ShadowModerationStatusSchema = z.enum([
  'pending',
  'approved',
  'needs_review',
  'rejected',
]);

/** Moderation state of a submitted feedback document. */
export type ShadowModerationStatus = z.infer<
  typeof ShadowModerationStatusSchema
>;

/**
 * A peer shadowing feedback document (one per reviewer / section / week).
 *
 * `content` is keyed by rubric field key; validate it against
 * `buildShadowContentSchema(getShadowRubric({version: rubricVersion}))`. Uses
 * `.loose()` to tolerate extra Firestore metadata fields.
 */
export const ShadowFeedbackDocumentSchema = z
  .object({
    /** Firebase Auth uid of the reviewer (also the document id). */
    reviewerUid: z.string(),
    /**
     * Firebase Auth uid of the section leader who taught this week, resolved
     * via `getTeachingSlUserIdForWeek` at write time (covers can change later).
     */
    revieweeUid: z.string(),
    /** Denormalized course id (matches the `{courseId}` path segment). */
    courseId: z.string(),
    /** Denormalized section id (matches the `{sectionId}` path segment). */
    sectionId: z.string(),
    /** Denormalized 1-based section week (matches the `{sectionWeek}` segment). */
    sectionWeek: z.number(),
    /** Draft (reviewer editing) or submitted (sent to moderation). */
    status: ShadowFeedbackStatusSchema,
    /** Rubric version this feedback was written against (see `shadowRubric.ts`). */
    rubricVersion: z.string(),
    /** Per-field values, keyed by rubric field key. */
    content: z.record(z.string(), ShadowFieldValueSchema),
    /** Id of the `assignments` doc that authorized this feedback. */
    assignmentId: z.string(),
    /** When the draft was first created (server time). */
    createdAt: FirestoreTimestampSchema,
    /** When the reviewer submitted; absent while still a draft. */
    submittedAt: FirestoreTimestampSchema.optional(),
    /**
     * When the submitted review is released to the reviewee (see
     * `getShadowReleaseAtMs`); the reviewer may undo it until then. Stamped by
     * the submit trigger. Absent while a draft, and on reviews submitted
     * before releases were held, which were delivered on submit.
     */
    releaseAt: FirestoreTimestampSchema.optional(),
    /** When the review was delivered to the reviewee; absent until then. */
    releasedAt: FirestoreTimestampSchema.optional(),
    /**
     * Denormalized moderation verdict, mirrored from the sibling verdict doc by
     * the trigger so reviewee-facing `collectionGroup` reads can filter on it
     * (and the read rule can gate on it) without a per-doc `get()`. Absent until
     * the feedback has been moderated. An approved review reads `pending` until
     * it is released, and only then `approved`, so the reviewee can read it
     * only from its release.
     */
    moderationStatus: ShadowModerationStatusSchema.optional(),
    /**
     * Denormalized moderation flags (e.g. `auto:contact-info`), mirrored from
     * the verdict doc by the trigger so the staff moderation queue can show why
     * an item was held without reading the sibling verdict doc. Absent until
     * moderated; empty when nothing was flagged.
     */
    moderationFlags: z.array(z.string()).optional(),
    /**
     * Reviewer's display name captured at submit time. Denormalized for the
     * received-feedback view; kept a discrete field so a future anonymous mode
     * can withhold it in one place.
     */
    reviewerName: z.string().optional(),
  })
  .loose();

/** A stored peer shadowing feedback document. */
export type ShadowFeedbackDocument = z.infer<
  typeof ShadowFeedbackDocumentSchema
>;

// ============================================================================
// Moderation verdict document (server-written)
// ============================================================================

/**
 * Server-written moderation verdict, a sibling of the feedback doc keyed by the
 * same reviewer uid. The client can read but never write this (mirrors
 * `studentReflectionModeration`). Uses `.loose()` for extra metadata.
 */
export const ShadowFeedbackModerationDocumentSchema = z
  .object({
    /** Current moderation state. */
    status: ShadowModerationStatusSchema,
    /** LLM model id used for the automated pre-filter, when applicable. */
    model: z.string().optional(),
    /** Machine/human flags (e.g. `auto:sentiment`, `harsh-tone`). */
    flags: z.array(z.string()).optional(),
    /**
     * Reason a reviewer's feedback was rejected. Shared with the REVIEWER, not
     * the reviewee. Absent unless `status === 'rejected'`.
     */
    rejectionReason: z.string().optional(),
    /** When the automated pre-filter last ran; absent if not yet checked. */
    checkedAt: FirestoreTimestampSchema.optional(),
    /** Server time of the last write to this verdict. */
    updatedAt: FirestoreTimestampSchema,
  })
  .loose();

/** A stored moderation verdict document. */
export type ShadowFeedbackModerationDocument = z.infer<
  typeof ShadowFeedbackModerationDocumentSchema
>;

/**
 * Must match the Cloud Functions export name (Firebase callable id) that lets
 * staff approve/reject a submitted feedback (server writes the verdict doc).
 */
export const MODERATE_SHADOW_FEEDBACK_FUNCTION_NAME = 'moderateshadowfeedback';

/** Callable request payload for `moderateshadowfeedback`. */
export const ModerateShadowFeedbackRequestSchema = z.object({
  /** Course the feedback belongs to. */
  courseId: z.string().min(1),
  /** Section the feedback is about. */
  sectionId: z.string().min(1),
  /** Section week (1-based SectionWeekId). */
  sectionWeek: z.number().int().positive(),
  /** Reviewer whose feedback is being moderated (feedback/verdict doc id). */
  reviewerUid: z.string().min(1),
  /** Approve releases to the reviewee; reject withholds it. */
  decision: z.enum(['approve', 'reject']),
  /**
   * Rejection reason, shared with the reviewer (not the reviewee). Ignored on
   * approve. Nullable because the callable client encodes an `undefined`
   * object value as `null`.
   */
  reason: z.string().nullish(),
});

/** Request payload for `moderateshadowfeedback`. */
export type ModerateShadowFeedbackRequest = z.infer<
  typeof ModerateShadowFeedbackRequestSchema
>;

// ============================================================================
// Paths
// ============================================================================

/**
 * Collection path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/shadowFeedback`
 */
export function getShadowFeedbackCollectionPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  return getSectionAnalysisWeekChildCollectionPath({
    courseId,
    sectionId,
    sectionWeek,
    leafSubcollection: SECTION_ANALYSIS_SHADOW_FEEDBACK_SUBCOLLECTION,
  });
}

/**
 * Document path (document id = reviewer uid):
 * `.../sectionWeeks/{sectionWeek}/shadowFeedback/{reviewerUid}`
 */
export function getShadowFeedbackDocumentPath({
  courseId,
  sectionId,
  sectionWeek,
  reviewerUid,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  reviewerUid: string;
}): string {
  return `${getShadowFeedbackCollectionPath({courseId, sectionId, sectionWeek})}/${reviewerUid}`;
}

/**
 * Moderation collection path:
 * `.../sectionWeeks/{sectionWeek}/shadowFeedbackModeration`
 */
export function getShadowFeedbackModerationCollectionPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  return getSectionAnalysisWeekChildCollectionPath({
    courseId,
    sectionId,
    sectionWeek,
    leafSubcollection:
      SECTION_ANALYSIS_SHADOW_FEEDBACK_MODERATION_SUBCOLLECTION,
  });
}

/**
 * Moderation document path (document id = reviewer uid, same as the feedback doc):
 * `.../sectionWeeks/{sectionWeek}/shadowFeedbackModeration/{reviewerUid}`
 */
export function getShadowFeedbackModerationDocumentPath({
  courseId,
  sectionId,
  sectionWeek,
  reviewerUid,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  reviewerUid: string;
}): string {
  return `${getShadowFeedbackModerationCollectionPath({courseId, sectionId, sectionWeek})}/${reviewerUid}`;
}

// ============================================================================
// Flags to course staff
// ============================================================================

/**
 * Who raised a flag to course staff: the reviewer, about the section they
 * shadowed, or the reviewee, about the review they received.
 */
export const ShadowStaffFlagRaisedBySchema = z.enum(['reviewer', 'reviewee']);

/** Who raised a flag to course staff. */
export type ShadowStaffFlagRaisedBy = z.infer<
  typeof ShadowStaffFlagRaisedBySchema
>;

/**
 * Document-id prefix of a reviewee's flag. The rest of the id is the reviewer
 * uid, so each review takes at most one reviewee flag. Firebase-generated uids
 * have no underscore, so the id never collides with a reviewer's own flag.
 */
export const SHADOW_REVIEWEE_FLAG_ID_PREFIX = 'reviewee__';

/**
 * A flag to course staff, with the reason its author gave. A sibling of the
 * feedback doc rather than a field on it, so each party's flag stays hidden
 * from the other: the reviewee can read approved feedback, and the reviewer
 * owns the feedback doc. Readable by its author and staff only. Its existence
 * is the flag, so un-flagging deletes the doc.
 */
export const ShadowStaffFlagDocumentSchema = z
  .object({
    /** Denormalized course id (matches the `{courseId}` path segment). */
    courseId: z.string(),
    /** Section the flag is about. */
    sectionId: z.string(),
    /** 1-based section week the flag is about. */
    sectionWeek: z.number(),
    /** Reviewer whose review the section-week got. */
    reviewerUid: z.string(),
    /** Section leader who taught the flagged section-week. */
    revieweeUid: z.string(),
    /** Id of the `assignments` doc the review was done under. */
    assignmentId: z.string(),
    /** Why the flag was raised, in its author's words. */
    reason: z.string(),
    /**
     * Who raised the flag. Absent on a reviewer's flag written before
     * reviewees could flag, so read absent as `reviewer`.
     */
    raisedBy: ShadowStaffFlagRaisedBySchema.optional(),
    /** Server time the flag was first raised; absent until resolved. */
    createdAt: FirestoreTimestampSchema.optional(),
    /** Server time of the last write to the flag. */
    updatedAt: FirestoreTimestampSchema.optional(),
  })
  .loose();

/** A stored staff-flag document. */
export type ShadowStaffFlagDocument = z.infer<
  typeof ShadowStaffFlagDocumentSchema
>;

/**
 * Staff-flag document path. A reviewer's flag is keyed by the reviewer uid
 * (same as the feedback doc); a reviewee's flag on that review is keyed by
 * {@link SHADOW_REVIEWEE_FLAG_ID_PREFIX} plus the reviewer uid.
 *
 * @param params - The flag's location.
 * @param params.courseId - Course of the reviewed section.
 * @param params.sectionId - The reviewed section.
 * @param params.sectionWeek - 1-based week of the review.
 * @param params.reviewerUid - Reviewer of the section-week.
 * @param params.raisedBy - Who raises the flag. Omitted ⇒ `reviewer`.
 * @return The flag document's path.
 * @throws Never.
 */
export function getShadowStaffFlagDocumentPath({
  courseId,
  sectionId,
  sectionWeek,
  reviewerUid,
  raisedBy = 'reviewer',
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  reviewerUid: string;
  raisedBy?: ShadowStaffFlagRaisedBy;
}): string {
  const collectionPath = getSectionAnalysisWeekChildCollectionPath({
    courseId,
    sectionId,
    sectionWeek,
    leafSubcollection: SECTION_ANALYSIS_SHADOW_STAFF_FLAGS_SUBCOLLECTION,
  });
  const flagId =
    raisedBy === 'reviewee'
      ? `${SHADOW_REVIEWEE_FLAG_ID_PREFIX}${reviewerUid}`
      : reviewerUid;
  return `${collectionPath}/${flagId}`;
}

// ============================================================================
// Threaded replies on a feedback document
// ============================================================================

/**
 * Subcollection of a feedback doc holding the back-and-forth between the
 * reviewer and reviewee. A distinct name (not a generic `replies`) so a future
 * collection-group query can target it without colliding with other features.
 */
export const SHADOW_FEEDBACK_REPLIES_SUBCOLLECTION =
  'shadowFeedbackReplies' as const;

/**
 * One threaded reply on a shadow feedback. Server-written (via the
 * `postshadowreply` callable) after moderation, so the client never writes it
 * directly. The parent's `reviewerUid`/`revieweeUid` are denormalized so the
 * read rule can gate visibility (the other party sees a reply only once it is
 * `approved`) without a cross-document `get()`. Uses `.loose()` for metadata.
 */
export const ShadowFeedbackReplyDocumentSchema = z
  .object({
    /** Firebase Auth uid of who wrote the reply (the reviewer or the reviewee). */
    authorUid: z.string(),
    /** Author's display name captured at post time, for the thread UI. */
    authorName: z.string().optional(),
    /** Reviewer of the parent feedback (denormalized for the read rule). */
    reviewerUid: z.string(),
    /** Reviewee of the parent feedback (denormalized for the read rule). */
    revieweeUid: z.string(),
    /** Denormalized course id. */
    courseId: z.string(),
    /** Denormalized section id of the parent feedback. */
    sectionId: z.string(),
    /** Denormalized 1-based section week of the parent feedback. */
    sectionWeek: z.number(),
    /** The reply text. */
    body: z.string(),
    /**
     * Moderation verdict. Only `approved` replies are visible to the other
     * party; the author always sees their own. Set by the server on post.
     */
    moderationStatus: ShadowModerationStatusSchema,
    /** Machine moderation flags (e.g. `auto:contact-info`) when held. */
    moderationFlags: z.array(z.string()).optional(),
    /** Moderation model/version stamp. */
    moderationModel: z.string().optional(),
    /** When the reply was posted (server time). */
    createdAt: FirestoreTimestampSchema,
  })
  .loose();

/** A stored threaded reply document. */
export type ShadowFeedbackReplyDocument = z.infer<
  typeof ShadowFeedbackReplyDocumentSchema
>;

/**
 * Must match the Cloud Functions export name (Firebase callable id) that posts
 * a moderated reply to a feedback. Client writes to the replies subcollection
 * are denied by rules, so this callable is the only way to add one.
 */
export const POST_SHADOW_REPLY_FUNCTION_NAME = 'postshadowreply';

/** Callable request payload for `postshadowreply`. */
export const PostShadowReplyRequestSchema = z.object({
  /** Course the feedback belongs to. */
  courseId: z.string().min(1),
  /** Section the feedback is about. */
  sectionId: z.string().min(1),
  /** 1-based section week. */
  sectionWeek: z.number().int().positive(),
  /** Reviewer whose feedback is being replied to (the feedback/parent doc id). */
  reviewerUid: z.string().min(1),
  /** The reply text. */
  body: z.string().min(1),
});

/** Request payload for `postshadowreply`. */
export type PostShadowReplyRequest = z.infer<
  typeof PostShadowReplyRequestSchema
>;

/** Callable response for `postshadowreply`. */
export const PostShadowReplyResponseSchema = z.object({
  /** The created reply's id. */
  replyId: z.string(),
  /** The moderation verdict — `approved` is visible to both parties immediately. */
  moderationStatus: ShadowModerationStatusSchema,
});

/** Response for `postshadowreply`. */
export type PostShadowReplyResponse = z.infer<
  typeof PostShadowReplyResponseSchema
>;

/**
 * Callable id for `moderateshadowreply` — staff (TA+) release or reject a reply
 * that was held for moderation. Must match the Cloud Functions export name.
 */
export const MODERATE_SHADOW_REPLY_FUNCTION_NAME = 'moderateshadowreply';

/** Callable request payload for `moderateshadowreply`. */
export const ModerateShadowReplyRequestSchema = z.object({
  /** Course the reply belongs to. */
  courseId: z.string().min(1),
  /** Section the parent feedback is about. */
  sectionId: z.string().min(1),
  /** 1-based section week of the parent feedback. */
  sectionWeek: z.number().int().positive(),
  /** Reviewer of the parent feedback (the feedback/parent doc id). */
  reviewerUid: z.string().min(1),
  /** Id of the held reply being moderated. */
  replyId: z.string().min(1),
  /** Approve reveals it to the other party (and notifies them); reject withholds it. */
  decision: z.enum(['approve', 'reject']),
});

/** Request payload for `moderateshadowreply`. */
export type ModerateShadowReplyRequest = z.infer<
  typeof ModerateShadowReplyRequestSchema
>;

/**
 * Replies collection path (under the feedback doc):
 * `.../shadowFeedback/{reviewerUid}/shadowFeedbackReplies`
 */
export function getShadowFeedbackRepliesCollectionPath({
  courseId,
  sectionId,
  sectionWeek,
  reviewerUid,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  reviewerUid: string;
}): string {
  return `${getShadowFeedbackDocumentPath({courseId, sectionId, sectionWeek, reviewerUid})}/${SHADOW_FEEDBACK_REPLIES_SUBCOLLECTION}`;
}

/** Single reply document path. */
export function getShadowFeedbackReplyDocumentPath({
  courseId,
  sectionId,
  sectionWeek,
  reviewerUid,
  replyId,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  reviewerUid: string;
  replyId: string;
}): string {
  return `${getShadowFeedbackRepliesCollectionPath({courseId, sectionId, sectionWeek, reviewerUid})}/${replyId}`;
}
